import { useEffect, useState } from "react";
import { MapPin, ArrowRight, Images, X, ChevronLeft, ChevronRight } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { TOUR_IMAGES, TOUR_GALLERIES, WHATSAPP_URL, trackWhatsAppConversion } from "../data/mediaAssets";
import { useReveal } from "../hooks/useReveal";

const TourCard = ({ tour, image, index, onOpen }) => {
    const { t } = useI18n();
    const ref = useReveal();

    return (
        <article
            ref={ref}
            data-testid={`tour-card-${index}`}
            className="reveal group bg-white border border-neutral-200 flex flex-col h-full transition-all duration-500 hover:border-brand-gold hover:shadow-[0_30px_60px_-20px_rgba(11,27,61,0.25)]"
            style={{ transitionDelay: `${index * 60}ms` }}
        >
            <button
                type="button"
                onClick={() => onOpen(index)}
                data-testid={`tour-open-gallery-${index}`}
                className="relative aspect-[4/3] overflow-hidden text-left"
                aria-label={t.tours.see_more}
            >
                <img
                    src={image.cover}
                    alt={tour.name}
                    className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                    loading="lazy"
                    data-testid={`tour-image-${index}`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy-deep/70 via-transparent to-transparent" />
                <div className="absolute top-4 left-4 bg-brand-gold text-brand-navy px-3 py-1 font-sans text-[10px] uppercase tracking-[0.3em]">
                    Tour {String(index + 1).padStart(2, "0")}
                </div>
                <div className="absolute bottom-4 right-4 bg-white/95 text-brand-navy px-3 py-2 font-sans text-[10px] uppercase tracking-[0.25em] flex items-center gap-2 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all">
                    <Images className="w-3.5 h-3.5" />
                    {t.tours.see_more}
                </div>
            </button>

            <div className="p-8 flex-1 flex flex-col">
                <p className="font-sans text-[11px] uppercase tracking-[0.35em] text-brand-gold mb-3">
                    {tour.tagline}
                </p>
                <h3
                    className="font-serif text-2xl md:text-3xl text-brand-navy leading-tight mb-4"
                    data-testid={`tour-name-${index}`}
                >
                    {tour.name}
                </h3>
                <p className="font-sans text-sm text-neutral-600 leading-relaxed mb-6">
                    {tour.description}
                </p>

                <div className="mb-6">
                    <p className="font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-400 mb-3">
                        · {t.tours.visited}
                    </p>
                    <ul className="space-y-2">
                        {tour.places.map((p, i) => (
                            <li
                                key={i}
                                className="flex items-start gap-2 font-sans text-sm text-brand-navy"
                            >
                                <MapPin className="w-3.5 h-3.5 text-brand-gold mt-0.5 shrink-0" />
                                <span>{p}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <button
                    type="button"
                    onClick={() => onOpen(index)}
                    data-testid={`tour-see-more-${index}`}
                    className="mb-3 inline-flex items-center justify-center gap-2 border border-neutral-300 hover:border-brand-gold hover:text-brand-gold text-brand-navy py-3 font-sans text-[11px] uppercase tracking-[0.28em] transition-all"
                >
                    <Images className="w-4 h-4" />
                    <span>{t.tours.see_more}</span>
                </button>

                <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={trackWhatsAppConversion}
                    data-testid={`tour-cta-${index}`}
                    className="mt-auto group/btn inline-flex items-center justify-center gap-3 border border-brand-navy bg-brand-navy hover:bg-brand-navy-hover text-white py-4 font-sans text-xs uppercase tracking-[0.25em] transition-all"
                >
                    <span>{t.tours.book}</span>
                    <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                </a>
            </div>
        </article>
    );
};

const TourModal = ({ open, tourIndex, onClose }) => {
    const { t } = useI18n();
    const [imgIdx, setImgIdx] = useState(0);

    const tour = tourIndex != null ? t.tours.items[tourIndex] : null;
    const gallery = tourIndex != null ? TOUR_GALLERIES[tourIndex] || [] : [];

    useEffect(() => {
        if (open) setImgIdx(0);
    }, [open, tourIndex]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowRight") setImgIdx((i) => Math.min(i + 1, gallery.length - 1));
            if (e.key === "ArrowLeft") setImgIdx((i) => Math.max(i - 1, 0));
        };
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", onKey);
        };
    }, [open, tourIndex, onClose, gallery.length]);

    if (!open || tourIndex == null || !tour) return null;

    const currentImage = gallery[imgIdx];
    const currentLabel = tour.places[imgIdx];

    const next = () => setImgIdx((i) => (i + 1) % gallery.length);
    const prev = () => setImgIdx((i) => (i - 1 + gallery.length) % gallery.length);

    return (
        <div
            data-testid="tour-modal"
            className="fixed inset-0 z-[80] flex items-center justify-center animate-fade-in"
            role="dialog"
            aria-modal="true"
        >
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-brand-navy-deep/90 backdrop-blur-md"
                onClick={onClose}
                data-testid="tour-modal-backdrop"
            />

            {/* Content */}
            <div className="relative z-10 w-[95%] max-w-6xl max-h-[92vh] bg-white overflow-y-auto shadow-2xl">
                {/* Header */}
                <div className="sticky top-0 z-10 flex items-start justify-between p-5 md:p-7 border-b border-neutral-200 bg-white">
                    <div>
                        <p className="font-sans text-[10px] uppercase tracking-[0.35em] text-brand-gold mb-1">
                            {tour.tagline}
                        </p>
                        <h3 className="font-serif text-brand-navy text-2xl md:text-3xl leading-tight">
                            {tour.name}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="tour-modal-close"
                        aria-label={t.tours.close}
                        className="p-2 border border-neutral-300 hover:border-brand-navy hover:bg-brand-navy hover:text-white transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Main image */}
                <div className="relative bg-brand-navy-deep aspect-[16/10] md:aspect-[16/9] overflow-hidden">
                    <img
                        key={imgIdx}
                        src={currentImage}
                        alt={currentLabel}
                        className="w-full h-full object-cover animate-fade-in"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-navy-deep/70 via-transparent to-transparent pointer-events-none" />

                    {/* Label */}
                    <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7 flex items-end justify-between text-white">
                        <div>
                            <div className="h-px w-10 bg-brand-gold mb-2" />
                            <p className="font-serif text-xl md:text-3xl">{currentLabel}</p>
                            <p className="font-sans text-[10px] uppercase tracking-[0.4em] mt-1 text-white/70">
                                {imgIdx + 1} / {gallery.length}
                            </p>
                        </div>
                    </div>

                    {/* Prev/Next */}
                    {gallery.length > 1 && (
                        <>
                            <button
                                onClick={prev}
                                data-testid="tour-modal-prev"
                                aria-label="Previous"
                                className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 flex items-center justify-center bg-white/90 hover:bg-brand-gold hover:text-white text-brand-navy transition-colors"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <button
                                onClick={next}
                                data-testid="tour-modal-next"
                                aria-label="Next"
                                className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 flex items-center justify-center bg-white/90 hover:bg-brand-gold hover:text-white text-brand-navy transition-colors"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </>
                    )}
                </div>

                {/* Thumbnails */}
                {gallery.length > 1 && (
                    <div className="p-5 md:p-7 border-b border-neutral-200">
                        <div className="grid grid-cols-3 md:grid-cols-5 gap-2 md:gap-3">
                            {gallery.map((src, i) => (
                                <button
                                    key={i}
                                    onClick={() => setImgIdx(i)}
                                    data-testid={`tour-modal-thumb-${i}`}
                                    className={`relative aspect-[4/3] overflow-hidden group transition-all ${
                                        i === imgIdx
                                            ? "ring-2 ring-brand-gold"
                                            : "ring-1 ring-neutral-200 hover:ring-brand-navy opacity-80 hover:opacity-100"
                                    }`}
                                >
                                    <img
                                        src={src}
                                        alt={tour.places[i]}
                                        className="w-full h-full object-cover"
                                    />
                                    <span className="absolute inset-x-0 bottom-0 bg-brand-navy-deep/70 text-white text-[9px] md:text-[10px] uppercase tracking-wider py-1 px-2 truncate">
                                        {tour.places[i]}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Details */}
                <div className="p-6 md:p-8 grid md:grid-cols-3 gap-8">
                    <div className="md:col-span-2">
                        <p className="font-sans text-neutral-700 leading-relaxed">
                            {tour.description}
                        </p>

                        <div className="mt-6">
                            <p className="font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-400 mb-3">
                                · {t.tours.visited}
                            </p>
                            <ul className="grid sm:grid-cols-2 gap-2">
                                {tour.places.map((p, i) => (
                                    <li
                                        key={i}
                                        className="flex items-start gap-2 font-sans text-sm text-brand-navy"
                                    >
                                        <MapPin className="w-3.5 h-3.5 text-brand-gold mt-0.5 shrink-0" />
                                        <span>{p}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div className="flex md:justify-end">
                        <a
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={trackWhatsAppConversion}
                            data-testid="tour-modal-cta"
                            className="w-full md:w-auto inline-flex items-center justify-center gap-3 bg-brand-gold hover:bg-brand-gold-hover text-white px-6 py-4 font-sans text-xs uppercase tracking-[0.25em] transition-all"
                        >
                            <span>{t.tours.book}</span>
                            <ArrowRight className="w-4 h-4" />
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

const Tours = () => {
    const { t } = useI18n();
    const [openIdx, setOpenIdx] = useState(null);

    return (
        <section
            id="tours"
            data-testid="tours-section"
            className="bg-brand-cream py-24 md:py-32 relative overflow-hidden"
        >
            <div className="absolute top-0 left-1/2 -translate-x-1/2 h-40 w-px bg-brand-gold/40" />

            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="text-center mb-16 md:mb-20 max-w-3xl mx-auto">
                    <p className="font-sans text-brand-gold text-xs uppercase tracking-[0.5em] mb-5">
                        — {t.tours.eyebrow}
                    </p>
                    <h2
                        data-testid="tours-title"
                        className="font-serif text-brand-navy text-4xl md:text-5xl lg:text-6xl leading-tight"
                    >
                        {t.tours.title}
                    </h2>
                    <p className="mt-6 font-sans text-neutral-600 leading-relaxed">
                        {t.tours.intro}
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {t.tours.items.map((tour, i) => (
                        <TourCard
                            key={i}
                            tour={tour}
                            image={TOUR_IMAGES[i]}
                            index={i}
                            onOpen={setOpenIdx}
                        />
                    ))}
                </div>
            </div>

            <TourModal
                open={openIdx !== null}
                tourIndex={openIdx}
                onClose={() => setOpenIdx(null)}
            />
        </section>
    );
};

export default Tours;
